import React, {useCallback, useEffect, useState} from "react";
import BasicGrid from "../../../grid/BasicGrid";
import {getSkills, getSkillTypes} from "../../../../service/dataService";
import {Button, ButtonToolbar, Form, Stack, toaster, Tooltip, Whisper} from "rsuite";
import SuccessNotification from "../../../notifications/SuccessNotification";
import {
    SKILL_ADD_ERROR,
    SKILL_ADD_SUCCESS,
    SKILL_EDIT_ERROR,
    SKILL_EDIT_SUCCESS,
} from "../../../../service/constants";
import ErrorNotification from "../../../notifications/ErrorNotification";
import AddAdminSkillModal from "../../../modals/admin/skills/AddAdminSkillModal";
import EditAdminSkillModal from "../../../modals/admin/skills/EditAdminSkillModal";
import SkillViewerModal from "../../../modals/skills/SkillViewerModal";
import SkillCleanupModal from "../../../modals/admin/skills/SkillCleanupModal";

const defaultColumns = [
    {
        key: 'id',
        label: 'Id',
    },
    {
        key: 'skillName',
        label: 'Skill Name',
        flexGrow: 2,
    },
    {
        key: 'skillType',
        label: 'Skill Type',
        flexGrow: 2,
    },
];

const AdminSkills = () => {
    const [skills, setSkills] = useState([]);
    const [skillTypes, setSkillTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    // add modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    // edit modal
    const [selectedSkill, setSelectedSkill] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);

    // skill viewer modal
    const [viewerModalOpen, setViewerModalOpen] = useState(false);

    // skill cleanup modal
    const [cleanupModalOpen, setCleanupModalOpen] = useState(false);

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const loadSkills = useCallback(() => {
        setLoading(true);
        getSkills().then((data) => {
                // format the data into InputPicker format for now
                setSkills(data);
            }
        );
        getSkillTypes().then((data) => {
                // format the data into InputPicker format for now
                setSkillTypes(data.map(ele => ({label: ele.skillTypeName, value: ele.skillTypeName})));
                setLoading(false);
            }
        );
    }, []);

    useEffect(() => {
       loadSkills();
    }, [loadSkills]);


    const setClickedSkill = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedSkill(rowData);
    },[]);

    const handleAddClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={SKILL_ADD_SUCCESS} />, { placement: 'topCenter' });
            loadSkills();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={SKILL_ADD_ERROR} />, { placement: 'topCenter' });
        }
        setAddModalOpen(false);
    },[loadSkills]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={SKILL_EDIT_SUCCESS} />, { placement: 'topCenter' });
            loadSkills();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={SKILL_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    },[loadSkills]);

    const handleViewerClose = useCallback(() => {
        setViewerModalOpen(false);
    }, []);

    const handleCleanupClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={"Skill Merge Success"} />, { placement: 'topCenter' });
            loadSkills();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={"Skill Merge Error"} />, { placement: 'topCenter' });
        }
        setCleanupModalOpen(false);
    }, [loadSkills]);

    return (
        <>
            <Stack spacing={2} direction='column' alignItems='flex-start' style={{paddingBottom: '0.5rem'}}>
                <p>Click to edit/delete skill.</p>
                <p>
                    Changes/removal will cascade to all user resumes with that existing skill.
                </p>
                <p>
                 <b>Warning:</b> Deleting a skill will remove the skill for ALL users.
                </p>
            </Stack>
            <Form.Group>
                <ButtonToolbar>
                    <Button appearance="primary"
                            onClick={() => setAddModalOpen(true)}
                            style={{marginBottom: '1rem'}}
                    >
                        Add Skill
                    </Button>
                    <Whisper
                        trigger="hover"
                        speaker={<Tooltip>The Skill Viewer shows all Skills currently tracked by this tool</Tooltip>}
                        placement="top"
                    >
                        <Button
                            appearance="primary"
                            style={{marginBottom: '1rem'}}
                            onClick={() => setViewerModalOpen(true)}
                        >
                            Skill Viewer
                        </Button>
                    </Whisper>
                    <Whisper
                        trigger="hover"
                        speaker={<Tooltip>The Skill Cleanup allows easy data curation</Tooltip>}
                        placement="top"
                    >
                        <Button
                            appearance="primary"
                            style={{marginBottom: '1rem'}}
                            onClick={() => setCleanupModalOpen(true)}
                        >
                            Skill Cleanup
                        </Button>
                    </Whisper>
                </ButtonToolbar>
            </Form.Group>

            <BasicGrid
                loading={loading}
                columns={columns}
                data={skills}
                handleClick={setClickedSkill}
                exportTitle="admin-skill-list"
            />
            <AddAdminSkillModal
                open={addModalOpen}
                handleClose={handleAddClose}
                skillTypes={skillTypes}
            />
            <EditAdminSkillModal
                open={editModalOpen}
                handleClose={handleEditClose}
                selectedSkill={selectedSkill}
                skillTypes={skillTypes}
            />
            <SkillViewerModal
                open={viewerModalOpen}
                handleClose={handleViewerClose}
                unformattedSkills={skills}
            />
            <SkillCleanupModal
                open={cleanupModalOpen}
                handleClose={handleCleanupClose}
                unformattedSkills={skills}
            />
        </>
    );
};

export default AdminSkills;